<template>
  <div id="breakdown-item-list">
    <div class="breakdown-item"
         v-if="approvedAtDealership.count">
      <div class="row">
        <div class="previous">
          <p class="item">
            {{ $t("services_approved_at_dealership") }}
          </p>
          <button class="toggle"
                  @click="toggleApprovedAtDealership()">
            <img class="chevron"
                 :class="{'open' : approvedAtDealershipExpanded}"
                 src="@/assets/images/chevron-down.svg"
                 aria-hidden="true">
          </button>
        </div>
        <p class="item">
          {{ approvedAtDealership.total }}
        </p>
      </div>
      <div v-if="approvedAtDealershipExpanded"
           class="list">
        <template v-for="service in servicesApprovedAtDealershipFiltered.filter(service => service.id !== '-1')">
          <!-- Parent Service -->
          <div :key="service.id" class="row">
            <p class="item">
              <span v-if="service.isStandAloneSublet === true && service.isSubletLabor !== true" >{{ $t("sublet") }} - </span>{{ service.name }}
            </p>
            <p v-if="getServiceDisplayPrice(service)"
               class="item">
              {{ getServiceDisplayPrice(service) }}
            </p>
          </div>
          <!-- Child Sublets -->
          <div v-if="service.subletServices && service.subletServices.length > 0"
               :key="service.id + '-sublets'"
               class="sublet-group">
            <div v-for="sublet in service.subletServices"
                 :key="sublet.id"
                 class="row sublet-row">
              <p class="item">
                <span v-if="sublet.isSubletLabor !== true" >{{ $t("sublet") }} - </span>{{ sublet.name }}
              </p>
              <p v-if="getServiceDisplayPrice(sublet)"
                 class="item">
                {{ getServiceDisplayPrice(sublet) }}
              </p>
            </div>
          </div>
        </template>
      </div>
      <div v-if="servicesApprovedAtDealershipFiltered.length"
           class="divider" />
    </div>
    <div class="breakdown-item">
      <div class="row bold">
        <div class="previous">
          <p class="item">
            {{ $t("estimate") }} ({{ firstApprovedServicesCount }})
          </p>
          <button v-if="firstApprovedServicesWithSublets.length"
                  class="toggle"
                  @click="toggleFirstApproved()">
            <img class="chevron"
                 :class="{'open' : firstApprovedExpanded}"
                 src="@/assets/images/chevron-down.svg"
                 aria-hidden="true">
          </button>
        </div>
        <p :class="{
              'item': allServicesSelected,
              'unconfirmed-item': !allServicesSelected
            }">
          {{ formatCurrency(this.$store.getters.total.inspection) }}
        </p>
      </div>
      <div v-if="firstApprovedExpanded && firstApprovedServicesWithSublets.length"
           class="list">
        <template v-for="service in firstApprovedServicesWithSublets">
          <!-- Parent Service -->
          <div :key="service.id" class="row">
            <p class="item">
              <span v-if="service.isStandAloneSublet === true && service.isSubletLabor !== true" >{{ $t("sublet") }} - </span>{{ service.name }}
            </p>
            <p v-if="getServiceDisplayPrice(service)"
               class="item">
              {{ getServiceDisplayPrice(service) }}
            </p>
          </div>
          <!-- Child Sublets -->
          <div v-if="service.subletServices && service.subletServices.length > 0"
               :key="service.id + '-sublets'"
               class="sublet-group">
            <div v-for="sublet in service.subletServices"
                 :key="sublet.id"
                 class="row sublet-row">
              <p class="item">
                <span v-if="sublet.isSubletLabor !== true" >{{ $t("sublet") }} - </span>{{ sublet.name }}
              </p>
              <p v-if="getServiceDisplayPrice(sublet)"
                 class="item">
                {{ getServiceDisplayPrice(sublet) }}
              </p>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { getTotal, formatCurrency, getServiceDisplayPrice, services } from '@/mixins.js'
  export default {
    name: 'BreakdownItem',
    mixins: [getTotal, formatCurrency, getServiceDisplayPrice, services],
    props: {
      totalInspection: {
        type: Number,
        required: true
      }
    },
    data() {
      return {
        firstApprovedExpanded: false,
        approvedAtDealershipExpanded: false
      }
    },
    computed: {
      approvedAtDealership() {
        let services = this.servicesApprovedAtDealershipFiltered.filter(service => service.id !== '-1')
        // Include sublet prices in total calculation
        let total = this.getTotal(services)
        services.forEach((service) => {
          if (service.subletServices && service.subletServices.length > 0) {
            service.subletServices.forEach((sublet) => {
              total += sublet.price || 0
            })
          }
        })
        return {
          services,
          count: services.length,
          total: this.formatCurrency(total)
        }
      },
      allServicesSelected() {
        return this.services()
      },
      // Get services with their subletServices attached (not flattened)
      firstApprovedServicesWithSublets() {
        const services = this.$store.state.services
        let result = []
        
        services.forEach((service) => {
          if (service.category !== '3' && service.category !== '4') {
            if (service.subServices) {
              service.subServices.forEach((subService) => {
                // Skip child sublets - they should only appear under their parent via subletServices
                if (subService.parentServiceLineIdForSublet !== null && subService.parentServiceLineIdForSublet !== undefined) {
                  return
                }
                if (subService.isSelected) {
                  result.push(subService)
                }
              })
            } else {
              // Skip child sublets - they should only appear under their parent via subletServices
              if (service.parentServiceLineIdForSublet !== null && service.parentServiceLineIdForSublet !== undefined) {
                return
              }
              if (service.isSelected) {
                if (service.category !== '1' && service.category !== '2') {
                  result.push(service)
                }
              }
            }
          }
        })
        
        return result
      },
      // Count includes parent services + their child sublets
      firstApprovedServicesCount() {
        let count = 0
        this.firstApprovedServicesWithSublets.forEach((service) => {
          count++ // count the parent service
          if (service.subletServices && service.subletServices.length > 0) {
            count += service.subletServices.length // count child sublets
          }
        })
        return count
      },
      // Get services approved at dealership without duplicates
      // Filters out services that are already child sublets of other services
      // and removes duplicate standalone sublets
      servicesApprovedAtDealershipFiltered() {
        const services = this.servicesApprovedAtDealership
        
        // Collect all child sublet IDs to filter them out from the main list
        const childSubletIds = new Set()
        services.forEach((service) => {
          if (service.subletServices && service.subletServices.length > 0) {
            service.subletServices.forEach((sublet) => {
              childSubletIds.add(sublet.id)
            })
          }
        })
        
        // Also filter out duplicate standalone sublets by base ID
        const seenBaseIds = new Map()
        const standaloneSubletDuplicateIds = new Set()
        
        services.forEach((service) => {
          if (service.isStandAloneSublet === true) {
            const numericMatch = service.id.match(/^(\d+)/)
            if (numericMatch) {
              const baseId = numericMatch[1]
              const hasSuffix = service.id.includes('-')
              
              if (seenBaseIds.has(baseId)) {
                const existing = seenBaseIds.get(baseId)
                const existingHasSuffix = existing.id.includes('-')
                
                if (hasSuffix && !existingHasSuffix) {
                  standaloneSubletDuplicateIds.add(service.id)
                } else if (!hasSuffix && existingHasSuffix) {
                  standaloneSubletDuplicateIds.add(existing.id)
                  seenBaseIds.set(baseId, service)
                } else {
                  standaloneSubletDuplicateIds.add(service.id)
                }
              } else {
                seenBaseIds.set(baseId, service)
              }
            }
          }
        })
        
        // Filter out child sublets (by parentServiceLineIdForSublet), child sublet IDs, and duplicate standalone sublets
        return services.filter((service) => 
          (service.parentServiceLineIdForSublet === null || service.parentServiceLineIdForSublet === undefined) &&
          !childSubletIds.has(service.id) && 
          !standaloneSubletDuplicateIds.has(service.id)
        )
      },
      ...mapGetters([
        'categoryServicesShownOnRoute',
        'categoriesShown',
        'servicesApprovedAtDealership',
        'firstApprovedServices',
        'isPass'
      ])
    },
    methods: {
      toggleApprovedAtDealership() {
        this.approvedAtDealershipExpanded = !this.approvedAtDealershipExpanded
      },
      toggleFirstApproved() {
        this.firstApprovedExpanded = !this.firstApprovedExpanded
      }
    }
  };
</script>

<style lang="scss" scoped>
  .breakdown-item {
    background-color: var(--white);
    box-shadow: var(--shadow-light);
    padding: 1rem;
  }

  .row {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &.indented {
      padding-left: 3rem;

      .item {
        margin: 0.5rem 0;
      }
    }

    .unconfirmed-item {
      color: var(--grey-text);
    }

    .previous {
      display: flex;
      align-items: center;

      .toggle {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0 0 0 1rem;
        border: none;
        padding: 1.5rem 1rem;
        background-color: var(--white);

        .chevron {
          transition: transform .2s ease-in-out;
          max-width: 2rem;

          &.open {
            transform: rotate(180deg);
            transition: transform .2s ease-in-out;
          }
        }
      }
    }
  }

  .list {
    background-color: var(--grey-light-background);
    padding: 1rem;
    
    .sublet-group {
      .sublet-row {
        padding: 0.25rem 0;
      }
    }
  }

  .divider {
    width: 100%;
    height: 2px;
    margin: 1rem 0;
    background-color: var(--grey-light-background)
  }
</style>
